import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, MessageCircle, CheckCircle, Mail } from "lucide-react";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { useCopyBank } from "@/hooks/useCopyBank";

// Mode-aware share copy — maps (mode, shareContext) → header labels
const SHARE_HEADERS = {
  birthday: { offer: "Share my birthday deal", itinerary: "Share my birthday itinerary", wishlist: "Share my birthday wishlist", default: "Share my birthday deal" },
  discover: { offer: "Share this with a friend", itinerary: "Share this itinerary", wishlist: "Share my deals wishlist", default: "Share this with a friend" },
};

/**
 * Returns mode-aware share title and description for a given context.
 * @param {"birthday"|"discover"} mode
 * @param {"offer"|"itinerary"|"wishlist"} context
 * @param {{ name, offerTitle, friendName, totalValue, offerCount }} data
 */
export function getShareCopy(mode, context, data = {}) {
  if (context === "offer") {
    const prefix = mode === "birthday"
      ? "Check out this birthday deal I found"
      : mode === "friends"
      ? "Found this birthday deal for you"
      : "Found this great deal you might like";
    const suffix = data.name ? ` — ${data.name}${data.offerTitle ? `: ${data.offerTitle}` : ""}` : "";
    return {
      title: `${prefix}${suffix}`,
      description: mode === "birthday"
        ? `Birthday deal: ${data.offerTitle || data.name || ""}`
        : mode === "friends"
        ? `Birthday deal for a friend: ${data.offerTitle || data.name || ""}`
        : `Great deal: ${data.offerTitle || data.name || ""}`,
    };
  }
  if (context === "itinerary") {
    const prefix = mode === "birthday"
      ? "Here's my birthday itinerary"
      : mode === "friends"
      ? "Here's a birthday itinerary I planned for you"
      : "I built this birthday itinerary for you";
    const suffix = data.friendName ? ` — ${data.friendName}'s BirthdayDeaLight Plan` : "";
    return {
      title: `${prefix}${suffix}`,
      description: data.offerCount ? `${data.offerCount} deals worth $${data.totalValue}!` : "",
    };
  }
  if (context === "wishlist") {
    return {
      title: mode === "birthday"
        ? `Check out my birthday wishlist — $${data.totalValue || 0} in free stuff!`
        : mode === "friends"
        ? `Birthday deals I picked for you — $${data.totalValue || 0} worth!`
        : `Check out these deals I saved — $${data.totalValue || 0} in savings!`,
      description: mode === "birthday" ? "BirthdayDeaLight wishlist" : mode === "friends" ? "Birthday deals for a friend" : "My saved deals wishlist",
    };
  }
  return { title: "", description: "" };
}

export const ShareSheet = ({ isOpen, onClose, url, title, description, businessId, businessName, shareContext = "offer" }) => {
  const { mode } = useTheme();
  const { getText } = useCopyBank();
  const [copied, setCopied] = useState(false);

  const logShare = useCallback(async (platform) => {
    try {
      await axios.post(`${API}/shares`, {
        business_id: businessId || "",
        business_name: businessName || title || "",
        platform,
        share_url: url,
      });
      // Award share points
      await axios.post(`${API}/share-points`, {
        type: "deal",
        business_id: businessId || "",
      });
    } catch {}
  }, [businessId, businessName, title, url]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    logShare("copy_link");
  };

  const handleSMS = () => {
    const body = encodeURIComponent(`${title}\n${url}`);
    window.open(`sms:?body=${body}`, "_blank");
    logShare("sms");
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`${title}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
    logShare("whatsapp");
  };

  const handleFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "width=600,height=400");
    logShare("facebook");
  };

  const handleTwitter = () => {
    const text = encodeURIComponent(`${title}`);
    window.open(`https://x.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, "_blank", "width=600,height=400");
    logShare("twitter");
  };

  const handleInstagram = async () => {
    try { await navigator.clipboard.writeText(url); } catch {}
    window.open("https://www.instagram.com/", "_blank");
    logShare("instagram");
  };

  const handleSnapchat = () => {
    window.open(`https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(url)}`, "_blank");
    logShare("snapchat");
  };

  const handleTelegram = () => {
    const text = encodeURIComponent(`${title}`);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${text}`, "_blank");
    logShare("telegram");
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${title}\n\n${url}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    logShare("email");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[200] flex items-end" onClick={onClose}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="w-full bg-white rounded-t-3xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800" data-testid="share-header">{getText("share_app")}</h3>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="share-close"><X className="w-5 h-5 text-slate-500" /></button>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-2">
              <button onClick={handleSMS} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-sms">
                <MessageCircle className="w-6 h-6 text-green-600" />
                <span className="text-[10px] text-slate-600 font-medium">Message</span>
              </button>
              <button onClick={handleWhatsApp} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-whatsapp">
                <svg className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span className="text-[10px] text-slate-600 font-medium">WhatsApp</span>
              </button>
              <button onClick={handleInstagram} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-instagram">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                  <defs><linearGradient id="ig" x1="0" y1="24" x2="24" y2="0"><stop stopColor="#feda75"/><stop offset=".25" stopColor="#fa7e1e"/><stop offset=".5" stopColor="#d62976"/><stop offset=".75" stopColor="#962fbf"/><stop offset="1" stopColor="#4f5bd5"/></linearGradient></defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig)" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="5" stroke="url(#ig)" strokeWidth="2"/>
                  <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig)"/>
                </svg>
                <span className="text-[10px] text-slate-600 font-medium">Instagram</span>
              </button>
              <button onClick={handleSnapchat} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-snapchat">
                <svg className="w-6 h-6 text-yellow-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.496.075.045.182.09.3.09.082 0 .17-.015.258-.045a.47.47 0 01.157-.03c.118 0 .295.037.435.15.21.168.24.39.24.525 0 .39-.39.66-.645.795a3.88 3.88 0 01-.315.15c-.095.044-.195.09-.264.135-.12.075-.165.135-.18.195.015.09.075.195.165.33l.017.028c.643 1.058 1.508 1.727 2.578 1.988.15.037.27.075.33.105.312.15.505.405.525.69.03.42-.36.72-.525.825a3.327 3.327 0 01-.51.255c-.18.075-.375.135-.57.195-.11.03-.216.075-.295.105-.13.06-.195.105-.225.165a.402.402 0 00-.015.15c.008.06.022.12.037.195a.66.66 0 01.015.12c0 .27-.165.495-.495.585-.195.053-.39.075-.585.098l-.09.012c-.21.027-.435.06-.66.12-.21.06-.39.165-.6.3-.315.195-.675.42-1.245.42-.03 0-.06 0-.105-.003a3.7 3.7 0 00-.135-.003c-.66 0-1.02.27-1.44.585-.315.24-.66.5-1.155.63-.09.024-.195.036-.3.036h-.015c-.105 0-.21-.012-.3-.036-.495-.13-.84-.39-1.155-.63-.42-.315-.78-.585-1.44-.585-.045 0-.09 0-.135.003l-.105.003c-.57 0-.93-.225-1.245-.42-.21-.135-.39-.24-.6-.3-.225-.06-.45-.093-.66-.12l-.09-.012c-.195-.023-.39-.045-.585-.098-.33-.09-.495-.315-.495-.585 0-.045.008-.09.015-.12.015-.075.03-.135.037-.195a.402.402 0 00-.015-.15c-.03-.06-.105-.105-.225-.165-.08-.03-.185-.075-.295-.105-.195-.06-.39-.12-.57-.195a3.327 3.327 0 01-.51-.255c-.165-.105-.555-.405-.525-.825.02-.285.213-.54.525-.69.06-.03.18-.068.33-.105 1.07-.26 1.935-.93 2.578-1.988l.017-.028c.09-.135.15-.24.165-.33-.015-.06-.06-.12-.18-.195-.069-.045-.17-.09-.264-.135a3.88 3.88 0 01-.315-.15c-.255-.135-.645-.405-.645-.795 0-.135.03-.357.24-.525.14-.113.317-.15.435-.15a.47.47 0 01.157.03c.088.03.176.045.258.045.118 0 .225-.045.3-.09-.008-.15-.018-.316-.03-.496l-.003-.06c-.104-1.628-.23-3.654.3-4.847C7.65 1.07 11.006.793 11.996.793h.21z"/></svg>
                <span className="text-[10px] text-slate-600 font-medium">Snapchat</span>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <button onClick={handleFacebook} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-facebook">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span className="text-[10px] text-slate-600 font-medium">Facebook</span>
              </button>
              <button onClick={handleTwitter} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-twitter">
                <svg className="w-6 h-6 text-slate-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                <span className="text-[10px] text-slate-600 font-medium">X</span>
              </button>
              <button onClick={handleTelegram} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-telegram">
                <svg className="w-6 h-6 text-sky-500" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                <span className="text-[10px] text-slate-600 font-medium">Telegram</span>
              </button>
              <button onClick={handleEmail} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition" data-testid="share-email">
                <Mail className="w-6 h-6 text-orange-500" />
                <span className="text-[10px] text-slate-600 font-medium">Email</span>
              </button>
            </div>

            <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-2">
              <span className="text-xs text-slate-500 truncate flex-1">{url}</span>
              <button onClick={handleCopy} className="flex items-center gap-1 text-xs font-semibold flex-shrink-0 px-3 py-1.5 rounded-lg transition-colors" data-testid="share-copy" style={{ color: copied ? "#10b981" : "#0284c7", background: copied ? "#ecfdf5" : "#f0f9ff" }}>
                {copied ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <p className="text-[10px] text-center text-slate-300 mt-3">+5 points per share</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
